import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import {
  getUser,
  getUserSpend,
  getUserVmLogs,
  getUserCronJobs,
  getConversations,
  getUserHibernateHistory,
  forceReleaseSlackWorkspace,
  type SlackForceReleaseResult,
  type SlackRevokeResult,
  patchUserFeatureFlags,
  createUserWorkspace,
  deleteUserWorkspace,
  applyConfigUserWorkspace,
  startUserWorkspace,
  stopUserWorkspace,
  getUserWorkspaceStatus,
  closeUserAccount,
  deleteUserVolume,
  restartUserCoworker,
  getUserRevenue,
  getUserRevenuePayments,
  getUserCreditGrants,
  getUserSubscriptionAudit,
  invoicePdfUrl,
  ApiRequestError,
  isLikelyRequestTimeoutMessage,
  type AdminConversationSessionSummary,
  type AdminSpendDailyRow,
  type AdminSpendUserResponse,
  type AdminUserRevenueResponse,
  type AdminRevenuePayment,
  type AdminSubscriptionAuditItem,
  type AdminCreditGrantsResponse,
  type AdminCronJobsResponse,
  type UserDetail,
  type VmLogSourceFilter,
  type VmLogsResponse,
  type HibernateHistoryResponse,
} from "@admin/lib/api";
import { usePoll } from "@admin/hooks/usePoll";
import { isServerError } from "@admin/lib/poll";
import {
  isWorkspaceProvisioning,
  resolveLiveWorkspaceStatus,
} from "@admin/lib/workspace-status";
import { formatCents, formatDate } from "@admin/lib/utils";
import { formatMicroUsd } from "@admin/lib/conversation-display";
import {
  CATEGORY_COLORS,
  CategoryDonut,
  DailyStackedChart,
  FALLBACK_COLOR,
  type SeriesDef,
} from "@admin/components/charts/SpendCharts";
import { SignupContextSection } from "@admin/components/SignupContextSection";
import { Card, CardContent, CardHeader, CardTitle } from "@admin/components/ui/card";
import { Button } from "@admin/components/ui/button";
import { UserHibernateChart } from "@admin/components/charts/HibernateCharts";
import { isoRangeForPreset, RangePicker, type RangePreset } from "@admin/components/RangePicker";

function formatBytes(n: number): string {
  const gb = n / 1024 ** 3;
  return `${gb.toFixed(2)} GB (${n.toLocaleString()} B)`;
}

function formatUsdCost(value: number): string {
  return `$${value.toFixed(2)}`;
}

function formatCredits(value: number): string {
  return new Intl.NumberFormat("en-US").format(value);
}

function formatWorkspaceOpsTunnel(publicIp: string): string {
  const host = publicIp.includes(":") ? `[${publicIp}]` : publicIp;
  return `ssh -N -L 9119:127.0.0.1:9119 -p 22222 -o ExitOnForwardFailure=yes root@${host}`;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">{children}</CardContent>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex flex-wrap gap-2">
      <span className="w-40 shrink-0 text-muted-fg">{label}</span>
      <span className="min-w-0 flex-1 break-all">{value}</span>
    </div>
  );
}

function HibernateHistorySection({ userId }: { userId: string }) {
  const [preset, setPreset] = useState<RangePreset>("7d");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [data, setData] = useState<HibernateHistoryResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const query = useMemo(() => {
    if (preset === "custom") {
      if (!customFrom || !customTo) return null;
      return { from: customFrom, to: customTo };
    }
    return isoRangeForPreset(preset);
  }, [preset, customFrom, customTo]);

  useEffect(() => {
    if (!query) return;
    setErr(null);
    getUserHibernateHistory(userId, query.from, query.to)
      .then(setData)
      .catch(() => setErr("Could not load hibernation history"));
  }, [userId, query]);

  return (
    <Section title="Hibernation history">
      <RangePicker
        preset={preset}
        customFrom={customFrom}
        customTo={customTo}
        onPreset={setPreset}
        onCustomFrom={setCustomFrom}
        onCustomTo={setCustomTo}
      />
      {err ? <p className="text-danger">{err}</p> : null}
      <UserHibernateChart data={data?.series ?? []} />
      {!data || data.events.length === 0 ? (
        <p className="text-xs text-muted-fg">No sleep/wake events in this range.</p>
      ) : (
        <ul className="max-h-48 space-y-1 overflow-y-auto font-mono text-xs">
          {data.events
            .slice()
            .reverse()
            .map((e) => (
              <li key={e.id}>
                {formatDate(e.occurredAt)} {e.kind}
                {e.reason ? ` (${e.reason})` : ""}
              </li>
            ))}
        </ul>
      )}
    </Section>
  );
}

function MessagingChannelsSection({
  user,
  releaseBusy,
  releaseErr,
  releaseOk,
  onForceReleaseSlack,
}: {
  user: UserDetail;
  releaseBusy: boolean;
  releaseErr: string | null;
  releaseOk: string | null;
  onForceReleaseSlack: () => void;
}) {
  const slackWorkspaces = user.slackWorkspaces ?? [];
  const telegramBots = user.telegramBots ?? [];
  const pairingSessions = user.telegramPairingSessions ?? [];
  const activeSlack = slackWorkspaces.find((w) => w.isActive);

  return (
    <Section title="Messaging channels">
      <div className="space-y-4">
        <div>
          <div className="text-xs font-medium text-muted-fg">Slack workspaces</div>
          <div className="mt-1 overflow-x-auto rounded-md border border-border">
            <table className="w-full text-left text-xs">
              <thead className="bg-muted/40 text-muted-fg">
                <tr>
                  <th className="px-2 py-1.5">Workspace</th>
                  <th className="px-2 py-1.5">Status</th>
                  <th className="px-2 py-1.5">Connected</th>
                  <th className="px-2 py-1.5">Last change</th>
                  <th className="px-2 py-1.5">Team ID</th>
                  <th className="px-2 py-1.5">Action</th>
                </tr>
              </thead>
              <tbody>
                {slackWorkspaces.map((w) => (
                  <tr key={w.teamId} className="border-t border-border">
                    <td className={`px-2 py-1.5 ${w.isActive ? "font-medium" : ""}`}>
                      {w.teamName ?? "-"}
                    </td>
                    <td className="px-2 py-1.5">
                      {w.isActive ? "Active" : "Disconnected"}
                    </td>
                    <td className="px-2 py-1.5">{formatDate(w.createdAt)}</td>
                    <td className="px-2 py-1.5">{formatDate(w.updatedAt)}</td>
                    <td className="px-2 py-1.5 font-mono">{w.teamId}</td>
                    <td className="px-2 py-1.5">
                      {w.isActive ? (
                        <button
                          type="button"
                          className="rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-50"
                          disabled={releaseBusy}
                          onClick={() => onForceReleaseSlack()}
                        >
                          {releaseBusy ? "Releasing..." : "Force release"}
                        </button>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                ))}
                {slackWorkspaces.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-2 py-3 text-muted-fg">
                      No Slack workspaces linked
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
          <p className="mt-1 text-xs text-muted-fg">
            History uses slack_workspaces.user_id. If a workspace was force-released and claimed by
            another account, it will no longer appear here. Disconnect time is last change when
            deactivated.
          </p>
          {activeSlack && releaseErr ? (
            <p className="mt-1 text-sm text-danger">{releaseErr}</p>
          ) : null}
          {activeSlack && releaseOk ? (
            <p className="mt-1 text-sm text-emerald-600">{releaseOk}</p>
          ) : null}
        </div>

        <div>
          <div className="text-xs font-medium text-muted-fg">Telegram bots</div>
          <div className="mt-1 overflow-x-auto rounded-md border border-border">
            <table className="w-full text-left text-xs">
              <thead className="bg-muted/40 text-muted-fg">
                <tr>
                  <th className="px-2 py-1.5">Bot</th>
                  <th className="px-2 py-1.5">Status</th>
                  <th className="px-2 py-1.5">Connected</th>
                  <th className="px-2 py-1.5">Last change</th>
                  <th className="px-2 py-1.5">Bot ID</th>
                  <th className="px-2 py-1.5">Allowed users</th>
                </tr>
              </thead>
              <tbody>
                {telegramBots.map((b) => (
                  <tr key={b.id} className="border-t border-border">
                    <td className={`px-2 py-1.5 ${b.isActive ? "font-medium" : ""}`}>
                      @{b.botUsername}
                    </td>
                    <td className="px-2 py-1.5">
                      {b.isActive ? "Active" : "Disconnected"}
                    </td>
                    <td className="px-2 py-1.5">{formatDate(b.createdAt)}</td>
                    <td className="px-2 py-1.5">{formatDate(b.updatedAt)}</td>
                    <td className="px-2 py-1.5 tabular-nums">{b.botId}</td>
                    <td className="px-2 py-1.5">{b.telegramAllowedUsers ?? "-"}</td>
                  </tr>
                ))}
                {telegramBots.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-2 py-3 text-muted-fg">
                      No Telegram bots
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <div className="text-xs font-medium text-muted-fg">Telegram QR pairing attempts</div>
          <div className="mt-1 overflow-x-auto rounded-md border border-border">
            <table className="w-full text-left text-xs">
              <thead className="bg-muted/40 text-muted-fg">
                <tr>
                  <th className="px-2 py-1.5">Suggested bot</th>
                  <th className="px-2 py-1.5">Display name</th>
                  <th className="px-2 py-1.5">Status</th>
                  <th className="px-2 py-1.5">Started</th>
                  <th className="px-2 py-1.5">Expires</th>
                  <th className="px-2 py-1.5">Ended</th>
                  <th className="px-2 py-1.5">Result</th>
                </tr>
              </thead>
              <tbody>
                {pairingSessions.map((s) => (
                  <tr
                    key={s.nonce}
                    className={`border-t border-border ${s.status === "expired" || s.status === "failed" ? "text-muted-fg" : ""}`}
                  >
                    <td className="px-2 py-1.5">@{s.suggestedUsername}</td>
                    <td className="px-2 py-1.5">{s.suggestedName}</td>
                    <td className={`px-2 py-1.5 ${s.status === "waiting" ? "font-medium" : ""}`}>
                      {s.status === "waiting"
                        ? "Waiting"
                        : s.status === "paired"
                          ? "Paired"
                          : s.status === "expired"
                            ? "Expired"
                            : "Failed"}
                    </td>
                    <td className="px-2 py-1.5">{formatDate(s.createdAt)}</td>
                    <td className="px-2 py-1.5">{formatDate(s.expiresAt)}</td>
                    <td className="px-2 py-1.5">
                      {s.completedAt ? formatDate(s.completedAt) : "-"}
                    </td>
                    <td className="px-2 py-1.5">
                      {s.botUsername ? `@${s.botUsername}` : "-"}
                    </td>
                  </tr>
                ))}
                {pairingSessions.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-2 py-3 text-muted-fg">
                      No QR pairing attempts
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
          <p className="mt-1 text-xs text-muted-fg">
            Connect with QR only. Manual token setup does not create pairing rows. Paired rows may
            duplicate the Telegram bots table above.
          </p>
        </div>
      </div>
    </Section>
  );
}

const CATEGORY_ORDER = ["llm", "tools", "infra"] as const;
const CATEGORY_LABEL: Record<string, string> = { llm: "LLM", tools: "Apps", infra: "Workspace" };

type DailyAcc = { day: string; llm: number; tools: number; infra: number };

function buildDaily(rows: AdminSpendDailyRow[]): DailyAcc[] {
  const map = new Map<string, DailyAcc>();
  for (const r of rows) {
    const cur = map.get(r.day) ?? { day: r.day, llm: 0, tools: 0, infra: 0 };
    if (r.category === "llm") cur.llm += r.costCents;
    else if (r.category === "tools") cur.tools += r.costCents;
    else if (r.category === "infra") cur.infra += r.costCents;
    map.set(r.day, cur);
  }
  return [...map.values()].sort((a, b) => (a.day < b.day ? -1 : 1));
}

function breakdown(rows: AdminSpendDailyRow[], category: string) {
  const map = new Map<string, number>();
  for (const r of rows) {
    if (r.category !== category) continue;
    const key = r.subcategory === "_pending" ? "(today)" : r.subcategory;
    map.set(key, (map.get(key) ?? 0) + r.costCents);
  }
  return [...map.entries()]
    .map(([key, costCents]) => ({ key, costCents }))
    .filter((r) => r.costCents > 0)
    .sort((a, b) => b.costCents - a.costCents);
}

function ConversationsSection({ userId }: { userId: string }) {
  const [items, setItems] = useState<AdminConversationSessionSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    getConversations({ userId, limit: 10 })
      .then((res) => {
        setItems(res.items);
        setTotal(res.total);
      })
      .catch((e) => setErr(e instanceof Error ? e.message : "Load failed"));
  }, [userId]);

  return (
    <Section title="Conversations">
      {err ? <p className="text-danger">{err}</p> : null}
      {items.length === 0 ? (
        <p className="text-muted-fg">No synced conversations</p>
      ) : (
        <ul className="space-y-2 text-sm">
          {items.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center gap-2">
              <Link to={`/conversations/${s.id}`} className="text-accent hover:underline">
                {s.title ?? s.agentSessionId.slice(0, 12)}
              </Link>
              <span className="text-xs text-muted-fg">{formatDate(s.startedAt)}</span>
              <span className="text-xs">{s.messageCount} msgs</span>
              <span className="text-xs tabular-nums">{formatMicroUsd(s.actualCostUsdMicro)}</span>
            </li>
          ))}
        </ul>
      )}
      {total > items.length ? (
        <Link to={`/conversations?userId=${userId}`} className="text-xs text-accent hover:underline">
          View all ({total})
        </Link>
      ) : total > 0 ? (
        <Link to="/conversations" className="text-xs text-accent hover:underline">
          Open conversations
        </Link>
      ) : null}
    </Section>
  );
}

const VM_LOG_SOURCES: VmLogSourceFilter[] = ["all", "agent", "errors", "bootstrap", "vm_bridge"];

const VM_LOG_PAGE_SIZE = 20;

function asString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

function asBool(value: unknown): boolean | null {
  return typeof value === "boolean" ? value : null;
}

function scheduleDisplay(job: Record<string, unknown>): string {
  const schedule = job.schedule;
  if (schedule && typeof schedule === "object" && !Array.isArray(schedule)) {
    const s = schedule as Record<string, unknown>;
    const display = asString(s.display);
    if (display) return display;
    const kind = asString(s.kind);
    if (kind === "cron" && asString(s.expr)) return String(s.expr);
    if (kind === "interval" && typeof s.minutes === "number") return `every ${s.minutes}m`;
    if (kind === "once" && asString(s.run_at)) return `once at ${s.run_at}`;
    if (kind) return kind;
  }
  return "-";
}

function skillsDisplay(job: Record<string, unknown>): string {
  const skills = job.skills;
  if (Array.isArray(skills) && skills.length > 0) {
    return skills.map(String).join(", ");
  }
  return "-";
}

function ScheduledTasksSection({ userId }: { userId: string }) {
  const [data, setData] = useState<AdminCronJobsResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [expandedPrompt, setExpandedPrompt] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getUserCronJobs(userId);
      setData(res);
      setErr(null);
    } catch (e) {
      const message =
        e instanceof ApiRequestError
          ? e.message
          : "Could not load scheduled tasks from the private workspace";
      setErr(message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Section title="Scheduled tasks">
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" size="sm" disabled={loading} onClick={() => void load()}>
          {loading ? "Loading..." : "Refresh"}
        </Button>
        <span className="text-xs text-muted-fg">
          Live read from the private workspace cron catalog (read-only).
        </span>
      </div>
      {err ? <p className="text-sm text-danger">{err}</p> : null}
      {!err && data && data.jobs.length === 0 ? (
        <p className="text-muted-fg">No scheduled tasks on this workspace.</p>
      ) : null}
      {data && data.jobs.length > 0 ? (
        <ul className="space-y-4">
          {data.jobs.map((job, idx) => {
            const id = asString(job.id) ?? `job-${idx}`;
            const name = asString(job.name) ?? "(unnamed)";
            const enabled = asBool(job.enabled);
            const state = asString(job.state);
            const prompt = asString(job.prompt);
            const route = data.slackRoutes[id];
            const deliveries = data.recentDeliveries.filter((d) => d.cronJobId === id).slice(0, 5);
            const showPrompt = expandedPrompt[id] === true;
            return (
              <li key={id} className="rounded border border-border p-3 space-y-2">
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="font-medium">{name}</span>
                  <span className="font-mono text-xs text-muted-fg">{id}</span>
                </div>
                <Row
                  label="Status"
                  value={
                    enabled == null && !state
                      ? "-"
                      : `${enabled == null ? "?" : enabled ? "enabled" : "disabled"}${
                          state ? ` / ${state}` : ""
                        }`
                  }
                />
                <Row label="Schedule" value={scheduleDisplay(job)} />
                <Row label="Next run" value={asString(job.next_run_at) ?? "-"} />
                <Row label="Last run" value={asString(job.last_run_at) ?? "-"} />
                <Row label="Last status" value={asString(job.last_status) ?? "-"} />
                <Row label="Created" value={asString(job.created_at) ?? "-"} />
                <Row label="Deliver" value={asString(job.deliver) ?? "-"} />
                <Row label="Origin" value={asString(job.origin) ?? "-"} />
                <Row label="Skills" value={skillsDisplay(job)} />
                <Row
                  label="Model"
                  value={
                    asString(job.model) || asString(job.provider)
                      ? `${asString(job.model) ?? "-"} / ${asString(job.provider) ?? "-"}`
                      : "-"
                  }
                />
                {job.repeat && typeof job.repeat === "object" && !Array.isArray(job.repeat) ? (
                  <Row
                    label="Repeat"
                    value={JSON.stringify(job.repeat)}
                  />
                ) : null}
                {asString(job.script) ? <Row label="Script" value={asString(job.script)!} /> : null}
                <Row
                  label="Slack route"
                  value={
                    route
                      ? `${route.teamId} / ${route.channelId} / ${route.threadTs}`
                      : "-"
                  }
                />
                {prompt ? (
                  <div className="space-y-1">
                    <button
                      type="button"
                      className="text-sm text-accent hover:underline"
                      onClick={() =>
                        setExpandedPrompt((prev) => ({ ...prev, [id]: !prev[id] }))
                      }
                    >
                      {showPrompt ? "Hide prompt" : "Show prompt"}
                    </button>
                    {showPrompt ? (
                      <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded bg-muted p-2 text-xs">
                        {prompt}
                      </pre>
                    ) : null}
                  </div>
                ) : null}
                {deliveries.length > 0 ? (
                  <div className="space-y-1">
                    <span className="text-muted-fg">Recent Slack deliveries</span>
                    <ul className="font-mono text-xs space-y-1">
                      {deliveries.map((d) => (
                        <li key={`${d.cronJobId}-${d.deliveredAt}`}>
                          {formatDate(d.deliveredAt)}
                          {d.channelId ? ` channel=${d.channelId}` : ""}
                          {d.threadTs ? ` thread=${d.threadTs}` : ""}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
    </Section>
  );
}

function VmLogsSection({ userId }: { userId: string }) {
  const [source, setSource] = useState<VmLogSourceFilter>("all");
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState<VmLogsResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      getUserVmLogs(userId, source, VM_LOG_PAGE_SIZE, offset)
        .then((res) => {
          if (!cancelled) {
            setData(res);
            setErr(null);
          }
        })
        .catch(() => {
          if (!cancelled) setErr("Could not load workspace logs");
        });
    };
    load();
    const timer =
      offset === 0 ? window.setInterval(load, 15_000) : undefined;
    return () => {
      cancelled = true;
      if (timer != null) window.clearInterval(timer);
    };
  }, [userId, source, offset]);

  const totalChunks = data?.totalChunks ?? 0;

  return (
    <Section title="Workspace logs (centralized)">
      <div className="flex flex-wrap gap-2">
        {VM_LOG_SOURCES.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => {
              setSource(s);
              setOffset(0);
            }}
            className={
              source === s
                ? "rounded-md bg-accent px-2 py-1 text-xs text-accent-fg"
                : "rounded-md border border-border px-2 py-1 text-xs text-muted-fg hover:text-fg"
            }
          >
            {s}
          </button>
        ))}
      </div>
      {data ? (
        <div className="space-y-2 text-xs text-muted-fg">
          <p>
            Sharing: {data.conversationSharingOptIn ? "on" : "off"}
            {data.serverStatus ? ` | workspace: ${data.serverStatus}` : " | no active workspace"}
            {data.telemetryTokenUpdatedAt
              ? ` | token rotated ${formatDate(data.telemetryTokenUpdatedAt)}`
              : " | no telemetry token"}
          </p>
          <p>
            Conversations:{" "}
            {data.conversationLastSyncAt
              ? `last sync ${formatDate(data.conversationLastSyncAt)} (${data.conversationSessionsSynced} sessions, ${data.conversationMessagesSynced} messages)`
              : "never synced"}
            {data.conversationLastError ? ` | error: ${data.conversationLastError}` : ""}
          </p>
          <p>
            Logs:{" "}
            {data.lastSyncAt
              ? `last sync ${formatDate(data.lastSyncAt)} (${data.bytesSynced} bytes)`
              : "never synced"}
            {data.lastError ? ` | error: ${data.lastError}` : ""}
            {totalChunks > 0
              ? ` | chunks ${offset + 1}-${Math.min(offset + VM_LOG_PAGE_SIZE, totalChunks)} of ${totalChunks} (newest first)`
              : ""}
          </p>
        </div>
      ) : null}
      {err ? (
        <p className="text-destructive text-sm">{err}</p>
      ) : !data?.text ? (
        <p className="text-muted-fg text-sm">No log data yet. Requires conversation sharing opt-in and a rebuilt workspace.</p>
      ) : (
        <div className="space-y-2">
          <pre className="max-h-96 overflow-auto rounded-md border border-border bg-muted/30 p-3 font-mono text-xs whitespace-pre-wrap break-all">
            {data.text}
          </pre>
          {totalChunks > 0 ? (
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={offset === 0}
                onClick={() => setOffset((o) => Math.max(0, o - VM_LOG_PAGE_SIZE))}
              >
                Newer
              </Button>
              <span className="text-xs text-muted-fg">
                {offset + 1}-{Math.min(offset + VM_LOG_PAGE_SIZE, totalChunks)} of {totalChunks}{" "}
                chunks (newest first)
              </span>
              <Button
                size="sm"
                variant="outline"
                disabled={offset + VM_LOG_PAGE_SIZE >= totalChunks}
                onClick={() => setOffset((o) => o + VM_LOG_PAGE_SIZE)}
              >
                Older
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </Section>
  );
}

function BillingSection({ userId }: { userId: string }) {
  const [summary, setSummary] = useState<AdminUserRevenueResponse | null>(null);
  const [payments, setPayments] = useState<AdminRevenuePayment[]>([]);
  const [grants, setGrants] = useState<AdminCreditGrantsResponse["items"]>([]);
  const [audit, setAudit] = useState<AdminSubscriptionAuditItem[]>([]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setErr(null);
    Promise.all([
      getUserRevenue(userId),
      getUserRevenuePayments(userId, { limit: 25, offset: 0 }),
      getUserCreditGrants(userId, { limit: 25, offset: 0 }),
      getUserSubscriptionAudit(userId, { limit: 25, offset: 0 }),
    ])
      .then(([s, p, g, a]) => {
        setSummary(s);
        setPayments(p.items);
        setGrants(g.items);
        setAudit(a.items);
      })
      .catch(() => setErr("Could not load billing"));
  }, [userId]);

  return (
    <Section title="Billing">
      {err ? <p className="text-danger">{err}</p> : null}
      {!summary ? (
        <p className="text-muted-fg">Loading...</p>
      ) : (
        <>
          <p className="text-xs text-muted-fg">Billing profile as last synced from Stripe</p>
          <Row label="Name" value={summary.billingProfile.billingName ?? "-"} />
          <Row label="Country" value={summary.billingProfile.billingCountry ?? "-"} />
          <Row label="VAT / tax ID" value={summary.billingProfile.billingVatId ?? "-"} />
          <Row
            label="Address"
            value={summary.billingProfile.billingAddressJson ?? "-"}
          />
          <Row
            label="Stripe customer"
            value={
              summary.stripeCustomerId ? (
                <a
                  href={`https://dashboard.stripe.com/customers/${summary.stripeCustomerId}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-accent hover:underline"
                >
                  {summary.stripeCustomerId}
                </a>
              ) : (
                "-"
              )
            }
          />

          {summary.subscription ? (
            <>
              <div className="pt-2 text-xs font-medium text-muted-fg">Subscription</div>
              <Row label="Plan" value={summary.subscription.planCode} />
              <Row label="Status" value={summary.subscription.status} />
              <Row
                label="Renews at"
                value={formatDate(summary.subscription.currentPeriodEnd)}
              />
              <Row
                label="Cancel at period end"
                value={summary.subscription.cancelAtPeriodEnd ? "yes" : "no"}
              />
              <Row
                label="Scheduled downgrade"
                value={summary.subscription.scheduledPlanCode ?? "-"}
              />
            </>
          ) : (
            <p className="text-muted-fg">No subscription</p>
          )}

          <div className="pt-2 text-xs font-medium text-muted-fg">Wallet</div>
          <Row label="Credits" value={formatCredits(summary.wallet.creditsCached)} />
          <Row
            label="Restricted"
            value={
              summary.wallet.restricted
                ? summary.wallet.restrictedReason ?? "yes"
                : "no"
            }
          />
          <Row
            label="Paused"
            value={summary.wallet.workspacePaused ? "yes" : "no"}
          />
          <Row
            label="Buckets (remaining)"
            value={`sub ${summary.wallet.buckets.subscription} / topup ${summary.wallet.buckets.topup} / reward ${summary.wallet.buckets.reward}`}
          />
          <Row
            label="Settlement debt (overspend)"
            value={formatCredits(summary.wallet.settlementDebtCredits)}
          />
          <p className="text-xs text-muted-fg">
            Credits used beyond grants; recovered on next top-up.
          </p>
          <Row
            label="Lifetime paid"
            value={formatCents(summary.totals.lifetimePaidCents)}
          />

          <div className="pt-3 text-xs font-medium text-muted-fg">Payments</div>
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-left text-xs">
              <thead className="bg-muted/40 text-muted-fg">
                <tr>
                  <th className="px-2 py-1.5">Date</th>
                  <th className="px-2 py-1.5">Type</th>
                  <th className="px-2 py-1.5">Amount</th>
                  <th className="px-2 py-1.5">VAT</th>
                  <th className="px-2 py-1.5">Status</th>
                  <th className="px-2 py-1.5">Invoice</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id} className="border-t border-border">
                    <td className="px-2 py-1.5">{formatDate(p.createdAt)}</td>
                    <td className="px-2 py-1.5">{p.type}</td>
                    <td className="px-2 py-1.5 tabular-nums">{formatCents(p.amountUsdCents)}</td>
                    <td className="px-2 py-1.5 font-mono">{p.billingVatId ?? "-"}</td>
                    <td className="px-2 py-1.5">{p.status}</td>
                    <td className="px-2 py-1.5">
                      {p.hasInvoicePdf ? (
                        <a
                          href={invoicePdfUrl(p.id)}
                          target="_blank"
                          rel="noreferrer"
                          className="text-accent hover:underline"
                        >
                          PDF
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                ))}
                {payments.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-2 py-3 text-muted-fg">
                      No payments
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          <div className="pt-3 text-xs font-medium text-muted-fg">Credit grants</div>
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-left text-xs">
              <thead className="bg-muted/40 text-muted-fg">
                <tr>
                  <th className="px-2 py-1.5">Date</th>
                  <th className="px-2 py-1.5">Credits</th>
                  <th className="px-2 py-1.5">Note</th>
                  <th className="px-2 py-1.5">Expires</th>
                </tr>
              </thead>
              <tbody>
                {grants.map((g) => (
                  <tr key={g.id} className="border-t border-border">
                    <td className="px-2 py-1.5">{formatDate(g.createdAt)}</td>
                    <td className="px-2 py-1.5 tabular-nums">{formatCredits(g.deltaCredits)}</td>
                    <td className="px-2 py-1.5">{g.note ?? g.bucketType ?? "-"}</td>
                    <td className="px-2 py-1.5">
                      {g.expiresAt ? formatDate(g.expiresAt) : "-"}
                    </td>
                  </tr>
                ))}
                {grants.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-2 py-3 text-muted-fg">
                      No grants
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          <div className="pt-3 text-xs font-medium text-muted-fg">Subscription history</div>
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-left text-xs">
              <thead className="bg-muted/40 text-muted-fg">
                <tr>
                  <th className="px-2 py-1.5">Date</th>
                  <th className="px-2 py-1.5">Event</th>
                  <th className="px-2 py-1.5">Plan</th>
                  <th className="px-2 py-1.5">Status</th>
                </tr>
              </thead>
              <tbody>
                {audit.map((a) => (
                  <tr key={a.id} className="border-t border-border">
                    <td className="px-2 py-1.5">{formatDate(a.createdAt)}</td>
                    <td className="px-2 py-1.5 font-mono">{a.eventType}</td>
                    <td className="px-2 py-1.5">
                      {a.fromPlanCode ?? "-"} → {a.toPlanCode ?? "-"}
                    </td>
                    <td className="px-2 py-1.5">
                      {a.fromStatus ?? "-"} → {a.toStatus ?? "-"}
                    </td>
                  </tr>
                ))}
                {audit.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-2 py-3 text-muted-fg">
                      No subscription events yet
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Section>
  );
}

function SpendBreakdownSection({ userId }: { userId: string }) {
  const [data, setData] = useState<AdminSpendUserResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    getUserSpend(userId)
      .then(setData)
      .catch(() => setErr("Could not load spend"));
  }, [userId]);

  const daily = useMemo(() => buildDaily(data?.rows ?? []), [data]);
  const models = useMemo(() => breakdown(data?.rows ?? [], "llm"), [data]);
  const tools = useMemo(() => breakdown(data?.rows ?? [], "tools"), [data]);

  const series: SeriesDef[] = CATEGORY_ORDER.map((cat) => ({
    key: cat,
    label: CATEGORY_LABEL[cat],
    color: CATEGORY_COLORS[cat] ?? FALLBACK_COLOR,
  }));

  const catCost = (cat: string) =>
    data?.byCategory.find((c) => c.category === cat)?.costCents ?? 0;
  const donutData = CATEGORY_ORDER.map((cat) => ({
    key: cat,
    label: CATEGORY_LABEL[cat],
    value: catCost(cat),
    color: CATEGORY_COLORS[cat] ?? FALLBACK_COLOR,
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Spend breakdown (last 30 days)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {err ? <p className="text-danger">{err}</p> : null}
        {!data ? (
          <p className="text-muted-fg">Loading...</p>
        ) : (
          <>
            <div className="flex flex-wrap gap-4">
              <div>
                <div className="text-xs text-muted-fg">Cost</div>
                <div className="text-lg font-semibold tabular-nums">
                  {formatCents(data.totals.costCents)}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-fg">USD cost</div>
                <div className="text-lg font-semibold tabular-nums">
                  {formatUsdCost(data.totals.usdCost)}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-fg">Credits</div>
                <div className="text-lg font-semibold tabular-nums">
                  {formatCredits(data.totals.credits)}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-fg">Price</div>
                <div className="text-lg font-semibold tabular-nums">
                  {formatCents(data.totals.priceCents)}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-fg">Margin</div>
                <div className="text-lg font-semibold tabular-nums">
                  {formatCents(data.totals.priceCents - data.totals.costCents)}
                </div>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <div className="text-xs text-muted-fg">Slack</div>
                <div className="font-medium tabular-nums">{formatUsdCost(data.byChannel.slack.usdCost)}</div>
                <ul className="mt-1 space-y-1 text-xs text-muted-fg">
                  {data.byChannel.slack.byActor.map((actor) => (
                    <li key={actor.actorExternalId} className="break-all">
                      {actor.displayName || actor.actorExternalId}: {formatUsdCost(actor.usdCost)}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-xs text-muted-fg">Telegram</div>
                <div className="font-medium tabular-nums">{formatUsdCost(data.byChannel.telegram.usdCost)}</div>
                <ul className="mt-1 space-y-1 text-xs text-muted-fg">
                  {data.byChannel.telegram.byActor.map((actor) => (
                    <li key={actor.actorExternalId} className="break-all">
                      {actor.displayName || actor.actorExternalId}: {formatUsdCost(actor.usdCost)}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-xs text-muted-fg">Platform</div>
                <div className="font-medium tabular-nums">{formatUsdCost(data.byChannel.platform.usdCost)}</div>
                <p className="mt-1 text-xs text-muted-fg">
                  Workspace {formatUsdCost(data.byChannel.platform.hetzner.usdCost)}, apps{" "}
                  {formatUsdCost(data.byChannel.platform.composio.usdCost)}
                </p>
              </div>
              <div>
                <div className="text-xs text-muted-fg">AI spend not tied to a chat</div>
                <div className="font-medium tabular-nums">
                  {formatUsdCost(data.byChannel.unattributedAi.usdCost)}
                </div>
              </div>
            </div>
            <DailyStackedChart data={daily} series={series} formatValue={formatCents} />
            <div className="grid gap-4 lg:grid-cols-2">
              <div>
                <div className="mb-2 text-xs text-muted-fg">By category</div>
                <CategoryDonut data={donutData} formatValue={formatCents} />
              </div>
              <div className="space-y-4">
                <div>
                  <div className="mb-1 text-xs text-muted-fg">By model</div>
                  {models.length === 0 ? (
                    <p className="text-muted-fg">None</p>
                  ) : (
                    <ul className="divide-y divide-border">
                      {models.slice(0, 8).map((m) => (
                        <li key={m.key} className="flex justify-between gap-3 py-1.5">
                          <span className="min-w-0 truncate font-mono text-xs" title={m.key}>
                            {m.key}
                          </span>
                          <span className="tabular-nums">{formatCents(m.costCents)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div>
                  <div className="mb-1 text-xs text-muted-fg">By app</div>
                  {tools.length === 0 ? (
                    <p className="text-muted-fg">None</p>
                  ) : (
                    <ul className="divide-y divide-border">
                      {tools.slice(0, 8).map((tlent) => (
                        <li key={tlent.key} className="flex justify-between gap-3 py-1.5">
                          <span className="min-w-0 truncate font-mono text-xs" title={tlent.key}>
                            {tlent.key}
                          </span>
                          <span className="tabular-nums">{formatCents(tlent.costCents)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function mergeWorkspaceStatusIntoUser(
  prev: UserDetail,
  status: {
    status: string;
    publicIp: string | null;
    serverType: string | null;
    region: string | null;
    lastPhoneHomeAt: string | null;
  },
): UserDetail {
  return {
    ...prev,
    server: prev.server
      ? {
          ...prev.server,
          status: status.status,
          publicIp: status.publicIp,
          serverType: status.serverType ?? prev.server.serverType,
          region: status.region ?? prev.server.region,
          lastPhoneHomeAt: status.lastPhoneHomeAt,
        }
      : status.status === "none"
        ? null
        : {
            id: "pending",
            status: status.status,
            region: status.region ?? "-",
            serverType: status.serverType ?? "-",
            publicIp: status.publicIp,
            error: null,
            lastPhoneHomeAt: status.lastPhoneHomeAt,
            hetznerServerId: null,
            hetznerVolumeId: null,
            createdAt: new Date().toISOString(),
          },
  };
}

function OpsModal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="ops-modal-title"
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-border bg-card p-6 shadow-lg"
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 id="ops-modal-title" className="text-lg font-semibold">
            {title}
          </h2>
          <button
            type="button"
            className="text-muted-fg hover:text-foreground"
            onClick={onClose}
            aria-label="Close"
          >
            x
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function CloseAccountSection({
  user,
  onClosed,
}: {
  user: UserDetail;
  onClosed: (updated: UserDetail) => void;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (user.closedAt) {
    return (
      <Section title="Account">
        <p className="text-sm text-amber-400">This account is closed.</p>
        <Row label="Closed at" value={formatDate(user.closedAt)} />
        <Row label="Closed by" value={user.closedBy ?? "-"} />
        <Row label="Reason" value={user.closedReason ?? "-"} />
      </Section>
    );
  }

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      await closeUserAccount(user.id, { reason: reason.trim(), confirmEmail: confirmEmail.trim() });
      const refreshed = await getUser(user.id);
      onClosed(refreshed);
      setOpen(false);
      setReason("");
      setConfirmEmail("");
    } catch (e) {
      setErr(e instanceof ApiRequestError ? e.message : "Failed to close account");
    } finally {
      setBusy(false);
    }
  }

  const canSubmit =
    reason.trim().length >= 3 &&
    confirmEmail.trim().toLowerCase() === user.email.toLowerCase() &&
    !busy;

  return (
    <Section title="Account">
      <p className="text-sm text-muted-fg">
        Ends the customer relationship: cancels Stripe subscription, deletes the workspace, disables
        AI spend, invalidates sessions, and blocks sign-in. The data volume is retained until you
        delete it separately.
      </p>
      <Button
        type="button"
        className="border border-red-500/50 bg-red-950/40 text-red-200 hover:bg-red-950/70"
        onClick={() => setOpen(true)}
      >
        Close account
      </Button>
      {open ? (
        <OpsModal title="Close account" onClose={() => !busy && setOpen(false)}>
          <ul className="mb-4 list-disc space-y-1 pl-5 text-sm text-muted-fg">
            <li>Stripe subscription ends immediately</li>
            <li>Private workspace is deleted</li>
            <li>AI spend key is disabled</li>
            <li>User cannot sign in again</li>
            <li>Data volume is retained until deleted separately</li>
          </ul>
          <label className="mb-3 block text-sm">
            <span className="mb-1 block font-medium">Reason (required)</span>
            <textarea
              className="min-h-20 w-full rounded border border-border bg-muted px-3 py-2 text-sm"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={busy}
            />
          </label>
          <label className="mb-4 block text-sm">
            <span className="mb-1 block font-medium">Type user email to confirm</span>
            <input
              type="email"
              className="w-full rounded border border-border bg-muted px-3 py-2 text-sm"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              placeholder={user.email}
              disabled={busy}
            />
          </label>
          {err ? <p className="mb-3 text-sm text-danger">{err}</p> : null}
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" disabled={busy} onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              className="border border-red-500/50 bg-red-950/40 text-red-200 hover:bg-red-950/70"
              disabled={!canSubmit}
              onClick={() => void submit()}
            >
              {busy ? "Closing..." : "Close account"}
            </Button>
          </div>
        </OpsModal>
      ) : null}
    </Section>
  );
}

function DeleteVolumeControls({
  user,
  hasServer,
  onDeleted,
}: {
  user: UserDetail;
  hasServer: boolean;
  onDeleted: () => void;
}) {
  const volumeId =
    user.workspaceStorage?.hetznerVolumeId ?? user.server?.hetznerVolumeId ?? null;
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [confirmPhrase, setConfirmPhrase] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  if (!volumeId) {
    return null;
  }

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      const result = await deleteUserVolume(user.id, {
        reason: reason.trim(),
        confirmPhrase: "DELETE VOLUME",
        confirmEmail: confirmEmail.trim(),
      });
      setOk(result.deleted ? `Volume ${result.volumeId} deleted.` : "No volume found to delete.");
      setOpen(false);
      onDeleted();
    } catch (e) {
      setErr(e instanceof ApiRequestError ? e.message : "Failed to delete volume");
    } finally {
      setBusy(false);
    }
  }

  const canSubmit =
    reason.trim().length >= 3 &&
    confirmPhrase === "DELETE VOLUME" &&
    confirmEmail.trim().toLowerCase() === user.email.toLowerCase() &&
    !busy;

  return (
    <div className="border-t border-border pt-3 mt-3 space-y-2">
      <p className="text-xs text-muted-fg">
        Permanently deletes the Hetzner data volume. Only available when no workspace is running.
      </p>
      <Button
        type="button"
        className="border border-red-500/50 bg-red-950/40 text-red-200 hover:bg-red-950/70"
        disabled={hasServer}
        onClick={() => setOpen(true)}
      >
        Delete volume
      </Button>
      {hasServer ? (
        <p className="text-xs text-muted-fg">Delete the workspace first before deleting the volume.</p>
      ) : null}
      {err ? <p className="text-sm text-danger">{err}</p> : null}
      {ok ? <p className="text-sm text-muted-fg">{ok}</p> : null}
      {open ? (
        <OpsModal title="Delete volume" onClose={() => !busy && setOpen(false)}>
          <p className="mb-4 text-sm text-muted-fg">
            This permanently wipes user data on disk. This cannot be undone.
          </p>
          <label className="mb-3 block text-sm">
            <span className="mb-1 block font-medium">Reason (required)</span>
            <textarea
              className="min-h-20 w-full rounded border border-border bg-muted px-3 py-2 text-sm"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={busy}
            />
          </label>
          <label className="mb-3 block text-sm">
            <span className="mb-1 block font-medium">Type DELETE VOLUME</span>
            <input
              type="text"
              className="w-full rounded border border-border bg-muted px-3 py-2 text-sm"
              value={confirmPhrase}
              onChange={(e) => setConfirmPhrase(e.target.value)}
              disabled={busy}
            />
          </label>
          <label className="mb-4 block text-sm">
            <span className="mb-1 block font-medium">Type user email to confirm</span>
            <input
              type="email"
              className="w-full rounded border border-border bg-muted px-3 py-2 text-sm"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              placeholder={user.email}
              disabled={busy}
            />
          </label>
          {err ? <p className="mb-3 text-sm text-danger">{err}</p> : null}
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" disabled={busy} onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              className="border border-red-500/50 bg-red-950/40 text-red-200 hover:bg-red-950/70"
              disabled={!canSubmit}
              onClick={() => void submit()}
            >
              {busy ? "Deleting..." : "Delete volume permanently"}
            </Button>
          </div>
        </OpsModal>
      ) : null}
    </div>
  );
}

function formatRelativeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return formatDate(iso);
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 48) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  return `${days}d ago`;
}

/**
 * Say what actually happened at Slack rather than a flat "released".
 *
 * The old copy claimed success unconditionally, which is precisely how a
 * revoke that never worked stayed invisible: the admin app holds no
 * decryption key, so every attempt failed and the operator saw "released".
 */
function describeSlackRelease(r: SlackForceReleaseResult): string {
  const parts: string[] = [];
  if (r.method === "uninstall") {
    // apps.uninstall revokes every install token, bot and user alike.
    parts.push("App uninstalled at Slack; all tokens revoked");
  } else if (r.method === "revoke") {
    const say = (v: SlackRevokeResult | null) =>
      v === null
        ? "none stored"
        : v === "revoked" || v === "already_revoked"
          ? "revoked"
          : `NOT revoked (${v})`;
    parts.push(`Bot token ${say(r.botRevoked)}, user token ${say(r.userRevoked)}`);
  } else {
    parts.push(r.alreadyInactive ? "Workspace was already released" : "No token to revoke");
  }
  parts.push(
    r.jobsCancelled != null
      ? `${r.jobsCancelled} queued job(s) cancelled`
      : "job cancellation FAILED",
  );
  parts.push(
    r.purgedJobs != null
      ? `${r.purgedJobs} Slack job row(s) purged`
      : "Slack job purge FAILED",
  );
  parts.push(
    r.purgedBackfills != null
      ? `${r.purgedBackfills} thread cache row(s) purged`
      : "thread cache purge FAILED",
  );
  parts.push(
    r.purgedCron != null
      ? `${r.purgedCron} scheduled delivery row(s) purged`
      : "scheduled delivery purge FAILED",
  );
  parts.push(
    r.composioRevoked != null
      ? `${r.composioRevoked} Slack app connection(s) revoked`
      : "Slack app connection revoke FAILED",
  );
  if (r.configBumped === false) parts.push("config revision bump FAILED");
  return `${parts.join(". ")}.`;
}

export default function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<UserDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [flagBusy, setFlagBusy] = useState(false);
  const [flagErr, setFlagErr] = useState<string | null>(null);
  const [releaseBusy, setReleaseBusy] = useState(false);
  const [releaseErr, setReleaseErr] = useState<string | null>(null);
  const [releaseOk, setReleaseOk] = useState<string | null>(null);
  const [wsBusy, setWsBusy] = useState(false);
  const [wsErr, setWsErr] = useState<string | null>(null);
  const [wsOk, setWsOk] = useState<string | null>(null);
  const [pollStatus, setPollStatus] = useState<string | null>(null);
  const [restartBusy, setRestartBusy] = useState(false);
  const [restartErr, setRestartErr] = useState<string | null>(null);
  const [restartOk, setRestartOk] = useState<string | null>(null);

  const serverStatus = user?.server?.status ?? null;
  const shouldWatchProvisioning =
    (pollStatus != null && isWorkspaceProvisioning(pollStatus)) ||
    (serverStatus != null && isWorkspaceProvisioning(serverStatus));

  const fetchWorkspaceStatus = useCallback(async () => {
    if (!id) throw new Error("missing user id");
    return getUserWorkspaceStatus(id);
  }, [id]);

  const workspacePoll = usePoll(fetchWorkspaceStatus, {
    enabled: Boolean(id && shouldWatchProvisioning),
    intervalMs: 10_000,
    immediate: true,
    backoff: { factor: 2, maxMs: 60_000 },
    jitterRatio: 0.2,
    retryOnceOn: isServerError,
    stopWhen: (s) => !isWorkspaceProvisioning(s.status),
  });

  useEffect(() => {
    if (!id) return;
    getUser(id)
      .then(setUser)
      .catch(() => setErr("User not found"));
    void getUserWorkspaceStatus(id)
      .then((s) => setPollStatus(s.status))
      .catch(() => undefined);
  }, [id]);

  useEffect(() => {
    const polled = workspacePoll.data;
    if (!polled) return;
    setPollStatus(polled.status);
    setUser((prev) => (prev ? mergeWorkspaceStatusIntoUser(prev, polled) : prev));
  }, [workspacePoll.data]);

  useEffect(() => {
    const polled = workspacePoll.data;
    if (!polled || isWorkspaceProvisioning(polled.status)) return;
    setPollStatus(null);
    setWsOk(null);
    setWsErr(null);
    void getUser(id!).then(setUser).catch(() => undefined);
  }, [workspacePoll.data, id]);

  async function runWorkspaceAction(
    action: "create" | "delete" | "apply-config" | "start" | "stop",
  ) {
    if (!id) return;
    setWsBusy(true);
    setWsErr(null);
    setWsOk(null);
    try {
      let result: { status: string };
      if (action === "create") {
        result = await createUserWorkspace(id, true);
      } else if (action === "delete") {
        result = await deleteUserWorkspace(id);
      } else if (action === "apply-config") {
        result = await applyConfigUserWorkspace(id);
      } else if (action === "start") {
        result = await startUserWorkspace(id);
      } else {
        result = await stopUserWorkspace(id);
      }
      setWsOk(`Workspace ${action}: ${result.status}`);
      if (isWorkspaceProvisioning(result.status)) {
        setPollStatus(result.status);
      } else {
        const refreshed = await getUser(id);
        setUser(refreshed);
      }
    } catch (e) {
      const msg =
        e instanceof ApiRequestError
          ? e.message
          : e instanceof Error
            ? e.message
            : "Workspace action failed";
      if (action === "apply-config" && isLikelyRequestTimeoutMessage(msg)) {
        setWsErr(
          "Request timed out. Rebuild may still be in progress - refreshing status.",
        );
        setPollStatus("provision_requested");
      } else {
        setWsErr(msg);
      }
    } finally {
      setWsBusy(false);
    }
  }
  async function forceReleaseSlack() {
    const activeSlack = user?.slackWorkspaces?.find((w) => w.isActive);
    if (!id || !activeSlack?.teamId) return;
    if (
      !window.confirm(
        "Release this Slack workspace so another John CEO account can install it?",
      )
    ) {
      return;
    }
    setReleaseBusy(true);
    setReleaseErr(null);
    setReleaseOk(null);
    try {
      const result = await forceReleaseSlackWorkspace(id, activeSlack.teamId);
      // Say what actually happened at Slack. The old copy claimed success
      // unconditionally, which is how a revoke that never worked stayed
      // invisible: the admin app cannot decrypt tokens, so every attempt
      // failed and the operator was told "released" regardless.
      setReleaseOk(describeSlackRelease(result));
      const refreshed = await getUser(id);
      setUser(refreshed);
    } catch (e) {
      setReleaseErr(
        e instanceof Error ? `Failed to release Slack workspace: ${e.message}` : "Failed to release Slack workspace",
      );
    } finally {
      setReleaseBusy(false);
    }
  }

  async function toggleFlag(
    key: "allow_data_collection_models" | "injection_screener_enforce",
    next: boolean,
  ) {
    if (!id || !user) return;
    setFlagBusy(true);
    setFlagErr(null);
    try {
      const flags = await patchUserFeatureFlags(id, { [key]: next });
      setUser({ ...user, featureFlags: flags });
    } catch {
      setFlagErr("Failed to update feature flags");
    } finally {
      setFlagBusy(false);
    }
  }

  if (err) {
    return (
      <div className="space-y-2">
        <p className="text-danger">{err}</p>
        <Link to="/users" className="text-sm text-accent hover:underline">
          Back to users
        </Link>
      </div>
    );
  }
  if (!user) return <p className="text-muted-fg">Loading...</p>;

  const flags = user.featureFlags ?? {
    allow_data_collection_models: false,
    injection_screener_enforce: false,
  };
  const accountClosed = Boolean(user.closedAt);

  async function runRestartCoworker() {
    if (!id) return;
    if (
      !window.confirm(
        "Restart John's software inside this workspace? Data is not deleted, but active work may be interrupted.",
      )
    ) {
      return;
    }
    setRestartBusy(true);
    setRestartErr(null);
    setRestartOk(null);
    try {
      await restartUserCoworker(id);
      setRestartOk("Restart requested. The coworker should be back within a minute.");
    } catch (e) {
      setRestartErr(
        e instanceof ApiRequestError ? e.message : "Failed to restart coworker",
      );
    } finally {
      setRestartBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div>
        <Link to="/users" className="text-sm text-accent hover:underline">
          Users
        </Link>
        <h1 className="mt-2 text-2xl font-semibold">{user.email}</h1>
        <p className="text-sm text-muted-fg">
          {user.id} - registered {formatDate(user.createdAt)}
        </p>
      </div>

      <SignupContextSection metadata={user} />

      <Section title="Tenant feature flags">
        {flagErr ? <p className="text-danger text-sm">{flagErr}</p> : null}
        <label className="flex items-start gap-2">
          <input
            type="checkbox"
            className="mt-0.5"
            disabled={flagBusy}
            checked={flags.allow_data_collection_models}
            onChange={(e) => toggleFlag("allow_data_collection_models", e.target.checked)}
          />
          <span>
            <span className="font-medium">allow_data_collection_models</span>
            <span className="block text-xs text-muted-fg">
              Permit models that may train on / collect prompts (e.g. qwen/*). Counsel checklist
              required before enabling for EU/Slack tenants.
            </span>
          </span>
        </label>
        <label className="mt-3 flex items-start gap-2">
          <input
            type="checkbox"
            className="mt-0.5"
            disabled={flagBusy}
            checked={flags.injection_screener_enforce}
            onChange={(e) => toggleFlag("injection_screener_enforce", e.target.checked)}
          />
          <span>
            <span className="font-medium">injection_screener_enforce</span>
            <span className="block text-xs text-muted-fg">
              Enforce assembled-text screening for this tenant when the platform ceiling is
              enforce. Leave off during the shadow rollout.
            </span>
          </span>
        </label>
      </Section>

      <Section title="Private workspace">
        {(() => {
          const polledStatus = workspacePoll.data?.status ?? null;
          const liveStatus = resolveLiveWorkspaceStatus(
            user.server?.status,
            polledStatus ?? pollStatus,
          );
          const asleep = liveStatus === "hibernated" || liveStatus === "waking";
          const provisioning = isWorkspaceProvisioning(liveStatus);
          const hasServer =
            asleep || (Boolean(user.server) && liveStatus !== "none");
          const ld = user.lastDelegatedWork;
          return (
            <>
              {user.server ? (
                <>
                  <Row label="Status" value={liveStatus ?? user.server.status} />
                  <Row label="Region" value={user.server.region} />
                  <Row label="Type" value={user.server.serverType} />
                  <Row label="IP address" value={user.server.publicIp ?? "-"} />
                  {user.server.publicIp ? (
                    <div className="flex flex-col gap-1">
                      <span className="w-40 shrink-0 text-muted-fg">SSH tunnel (workspace ops UI)</span>
                      <code className="select-all rounded bg-muted px-2 py-1 font-mono text-xs break-all">
                        {formatWorkspaceOpsTunnel(user.server.publicIp)}
                      </code>
                      <p className="text-xs text-muted-fg">
                        Leave the tunnel open (no output is normal), then open{" "}
                        <a
                          href="http://localhost:9119"
                          target="_blank"
                          rel="noreferrer"
                          className="text-accent hover:underline"
                        >
                          http://localhost:9119
                        </a>
                        . If SSH times out, add your public IP to the Hetzner firewall (see
                        docs/constants.md).
                      </p>
                    </div>
                  ) : null}
                  <Row label="Hetzner ID" value={user.server.hetznerServerId ?? "-"} />
                  <Row label="Hetzner volume ID" value={user.server.hetznerVolumeId ?? "-"} />
                  <Row
                    label="Last phone home"
                    value={
                      user.server.lastPhoneHomeAt
                        ? formatDate(user.server.lastPhoneHomeAt)
                        : "-"
                    }
                  />
                  {user.server.error && <Row label="Error" value={user.server.error} />}
                </>
              ) : asleep ? (
                <Row label="Status" value={liveStatus ?? "hibernated"} />
              ) : (
                <p className="text-muted-fg">No active server</p>
              )}

              <div className="border-t border-border pt-3 mt-3 space-y-2">
                <Row
                  label="Last delegated work"
                  value={
                    ld?.at
                      ? `${formatRelativeAgo(ld.at)} (${ld.source ?? "?"}) - ${formatDate(ld.at)}`
                      : "No recorded activity"
                  }
                />
                {ld?.byChannel.slack ? (
                  <Row
                    label="Last Slack"
                    value={`${formatRelativeAgo(ld.byChannel.slack)} - ${formatDate(ld.byChannel.slack)}`}
                  />
                ) : null}
                {ld?.byChannel.telegram ? (
                  <Row
                    label="Last Telegram"
                    value={`${formatRelativeAgo(ld.byChannel.telegram)} - ${formatDate(ld.byChannel.telegram)}`}
                  />
                ) : null}
                {ld?.byChannel.email ? (
                  <Row
                    label="Last email"
                    value={`${formatRelativeAgo(ld.byChannel.email)} - ${formatDate(ld.byChannel.email)}`}
                  />
                ) : null}
                {!ld?.conversationSharingOptIn ? (
                  <p className="text-xs text-muted-fg">
                    Conversation sharing is off for this user - Telegram activity may be missing.
                    Slack timestamps still come from the message queue.
                  </p>
                ) : null}
                {user.slackQueue && user.slackQueue.pending > 0 ? (
                  <p className="text-xs text-muted-fg">
                    Slack queue pending: {user.slackQueue.pending}. Messages during rebuild are
                    queued and will process when the bridge reconnects.
                  </p>
                ) : null}
              </div>

              <div className="border-t border-border pt-3 mt-3 space-y-2">
                <Row
                  label="Last hibernated"
                  value={
                    user.hibernation?.lastHibernateAt
                      ? `${formatRelativeAgo(user.hibernation.lastHibernateAt)} - ${formatDate(user.hibernation.lastHibernateAt)}`
                      : "-"
                  }
                />
                <Row
                  label="Last woke"
                  value={
                    user.hibernation?.lastWakeAt
                      ? `${formatRelativeAgo(user.hibernation.lastWakeAt)} - ${formatDate(user.hibernation.lastWakeAt)}`
                      : "-"
                  }
                />
                <Row label="Wake reason" value={user.hibernation?.lastWakeReason ?? "-"} />
                <Row
                  label="Last hibernate skip"
                  value={user.hibernation?.lastHibernateSkipReason ?? "-"}
                />
              </div>

              <div className="border-t border-border pt-3 mt-3 flex flex-wrap gap-2">
                {!accountClosed && !hasServer && !asleep ? (
                  <Button
                    type="button"
                    disabled={wsBusy || provisioning}
                    onClick={() => void runWorkspaceAction("create")}
                  >
                    Create workspace
                  </Button>
                ) : null}
                {!accountClosed && hasServer && !provisioning && !asleep ? (
                  <>
                    <Button
                      type="button"
                      disabled={wsBusy}
                      onClick={() => {
                        if (
                          !window.confirm(
                            "Apply and rebuild this user's private workspace? Slack messages will be queued until it is ready again.",
                          )
                        ) {
                          return;
                        }
                        void runWorkspaceAction("apply-config");
                      }}
                    >
                      Apply and Rebuild
                    </Button>
                    {liveStatus === "running" ? (
                      <>
                        <Button
                          type="button"
                          variant="outline"
                          disabled={restartBusy || wsBusy || !user.vmBridge?.liveConnected}
                          onClick={() => void runRestartCoworker()}
                        >
                          Restart coworker
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          disabled={wsBusy}
                          onClick={() => void runWorkspaceAction("stop")}
                        >
                          Stop
                        </Button>
                      </>
                    ) : null}
                    {liveStatus === "stopped" ? (
                      <Button
                        type="button"
                        variant="outline"
                        disabled={wsBusy}
                        onClick={() => void runWorkspaceAction("start")}
                      >
                        Start
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      variant="outline"
                      disabled={wsBusy}
                      onClick={() => {
                        if (
                          !window.confirm(
                            "Delete this user's private workspace? The volume is retained.",
                          )
                        ) {
                          return;
                        }
                        void runWorkspaceAction("delete");
                      }}
                    >
                      Delete workspace
                    </Button>
                  </>
                ) : null}
                {provisioning ? (
                  <p className="text-sm text-muted-fg">
                    Workspace is provisioning ({liveStatus})... polling every 10s.
                  </p>
                ) : null}
                {workspacePoll.error && provisioning ? (
                  <p className="text-danger text-sm">
                    Status refresh failed; retrying every 10s.
                  </p>
                ) : null}
              </div>
              {restartErr ? <p className="text-danger text-sm">{restartErr}</p> : null}
              {restartOk ? <p className="text-sm text-muted-fg">{restartOk}</p> : null}
              {!user.vmBridge?.liveConnected && liveStatus === "running" && !accountClosed ? (
                <p className="text-xs text-muted-fg">
                  Restart coworker requires the workspace bridge to be connected.
                </p>
              ) : null}
              <DeleteVolumeControls
                user={user}
                hasServer={hasServer}
                onDeleted={() => {
                  if (!id) return;
                  void getUser(id).then(setUser).catch(() => undefined);
                }}
              />
              {wsErr ? <p className="text-danger text-sm">{wsErr}</p> : null}
              {wsOk ? <p className="text-sm text-muted-fg">{wsOk}</p> : null}
            </>
          );
        })()}
      </Section>

      {id ? <HibernateHistorySection userId={id} /> : null}

      <MessagingChannelsSection
        user={user}
        releaseBusy={releaseBusy}
        releaseErr={releaseErr}
        releaseOk={releaseOk}
        onForceReleaseSlack={() => void forceReleaseSlack()}
      />

      {user.vmBridge ? (
        <Section title="Slack workspace bridge">
          <Row label="Live connected" value={user.vmBridge.liveConnected ? "yes" : "no"} />
          <Row
            label="Last connected"
            value={
              user.vmBridge.bridgeLastConnectedAt
                ? formatDate(user.vmBridge.bridgeLastConnectedAt)
                : "-"
            }
          />
          <Row
            label="Last disconnected"
            value={
              user.vmBridge.bridgeLastDisconnectedAt
                ? formatDate(user.vmBridge.bridgeLastDisconnectedAt)
                : "-"
            }
          />
          <Row label="Disconnect count" value={String(user.vmBridge.bridgeDisconnectCount)} />
        </Section>
      ) : null}

      {user.slackQueue ? (
        <Section title="Slack message queue">
          <Row label="Pending" value={String(user.slackQueue.pending)} />
          <Row label="Processing" value={String(user.slackQueue.processing)} />
          <Row label="Done (retained)" value={String(user.slackQueue.done)} />
          <Row label="Cancelled" value={String(user.slackQueue.cancelled)} />
          <Row label="Dead" value={String(user.slackQueue.dead)} />
          <Row
            label="Oldest pending"
            value={
              user.slackQueue.oldestPendingAt
                ? formatDate(user.slackQueue.oldestPendingAt)
                : "-"
            }
          />
          <p className="mt-2 text-sm text-muted-fg">
            <Link to="/slack-queue" className="text-accent hover:underline">
              Fleet queue dashboard
            </Link>
          </p>
        </Section>
      ) : null}

      <ScheduledTasksSection userId={user.id} />

      <Section title="Workspace storage">
        {user.workspaceStorage ? (
          <>
            <Row label="Hetzner volume ID" value={user.workspaceStorage.hetznerVolumeId ?? "-"} />
            <Row label="Provisioned" value={user.workspaceStorage.provisionedGb != null ? `${user.workspaceStorage.provisionedGb} GB` : "-"} />
            <Row label="Hetzner status" value={user.workspaceStorage.hetznerStatus ?? "-"} />
            <Row
              label="Attached server ID"
              value={user.workspaceStorage.attachedServerId ?? "detached"}
            />
            <Row
              label="Delete protected"
              value={user.workspaceStorage.deleteProtected == null ? "-" : user.workspaceStorage.deleteProtected ? "yes" : "no"}
            />
            <Row
              label="Used"
              value={user.workspaceStorage.usedBytes != null ? formatBytes(user.workspaceStorage.usedBytes) : "never reported"}
            />
            <Row
              label="Available"
              value={user.workspaceStorage.availableBytes != null ? formatBytes(user.workspaceStorage.availableBytes) : "-"}
            />
            <Row
              label="Total (used + available)"
              value={
                user.workspaceStorage.totalBytes != null
                  ? formatBytes(user.workspaceStorage.totalBytes)
                  : "-"
              }
            />
            <Row
              label="Reported at"
              value={user.workspaceStorage.reportedAt ? formatDate(user.workspaceStorage.reportedAt) : "-"}
            />
            <Row
              label="Hetzner synced at"
              value={user.workspaceStorage.syncedAt ? formatDate(user.workspaceStorage.syncedAt) : "-"}
            />
            {user.workspaceStorage.usageStale ? (
              <p className="text-xs text-amber-400">Usage estimate may be stale</p>
            ) : null}
          </>
        ) : (
          <p className="text-muted-fg">No workspace volume</p>
        )}
      </Section>

      <Section title="AI spend cache">
        {user.spend ? (
          <>
            <Row label="Usage" value={formatCents(user.spend.usageCents)} />
            <Row label="Limit" value={formatCents(user.spend.limitCents)} />
            <Row label="Remaining" value={formatCents(user.spend.remainingCents)} />
            <Row label="Cached at" value={formatDate(user.spend.cachedAt)} />
          </>
        ) : (
          <p className="text-muted-fg">No spend cache</p>
        )}
      </Section>

      {id ? <BillingSection userId={id} /> : null}
      <CloseAccountSection
        user={user}
        onClosed={(updated) => {
          setUser(updated);
        }}
      />
      {id ? <SpendBreakdownSection userId={id} /> : null}
      {id ? <ConversationsSection userId={id} /> : null}

      <Section title="Agent config">
        {user.agentConfig ? (
          <>
            <Row label="Locale" value={user.agentConfig.locale} />
            <Row label="Timezone" value={user.agentConfig.timezone} />
            <Row label="Free model" value={user.agentConfig.freeModel ? "yes" : "no"} />
            <Row label="Models" value={user.agentConfig.modelMapJson} />
            <Row
              label="Persona"
              value={<pre className="whitespace-pre-wrap text-xs">{user.agentConfig.personaMd}</pre>}
            />
          </>
        ) : (
          <p className="text-muted-fg">No agent config</p>
        )}
      </Section>

      <Section title="Composio connections">
        {user.connections.length === 0 ? (
          <p className="text-muted-fg">None</p>
        ) : (
          <ul className="space-y-2">
            {user.connections.map((c) => (
              <li key={`${c.toolkit}-${c.updatedAt}`}>
                {c.toolkit}: {c.status}
                {c.connectedAt ? ` (since ${formatDate(c.connectedAt)})` : ""}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <VmLogsSection userId={user.id} />

      <Section title="Provision events (latest 50)">
        {user.provisionEvents.length === 0 ? (
          <p className="text-muted-fg">None</p>
        ) : (
          <ul className="max-h-64 space-y-2 overflow-y-auto font-mono text-xs">
            {user.provisionEvents.map((e) => (
              <li key={e.id}>
                {formatDate(e.createdAt)} {e.kind}
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}
